import { Injectable, effect, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { Socket, connect } from 'socket.io-client';
import { MessageService } from 'primeng/api';
import { environment } from '../../environments/environment';
import { AuthService } from '../login/auth.service';
import { SidebarService } from '../shared/sidebar/sidebar.service';

export interface ChatMessagePayload {
  chatId: number;
  messageId: number;
  body: string;
  sentAt: string;
  authorId: number;
  authorName: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatRealtimeService {
  private authService = inject(AuthService);
  private messageService = inject(MessageService);
  private sidebarService = inject(SidebarService);
  private socket: Socket | null = null;

  messageReceived = new Subject<ChatMessagePayload>();

  constructor() {
    effect(() => {
      const jwt = this.authService.jwt();
      if (jwt) {
        this.connect(jwt);
      } else {
        this.disconnect();
      }
    });
  }

  sendChatMessage(chatId: number, body: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket?.connected) {
        reject(new Error('Socket is not connected.'));
        return;
      }

      socket.emit('chatMessage', chatId, body, (response: { success: boolean }) => {
        if (response.success) {
          resolve();
        } else {
          reject(new Error('Message could not be sent.'));
        }
      });
    });
  }

  private connect(jwt: string): void {
    if (this.socket?.connected) return;

    this.disconnect();
    this.socket = connect(environment.baseUrl, {
      extraHeaders: {
        Authorization: jwt
      }
    });

    this.socket.on('chatMessage', async (payload: ChatMessagePayload) => {
      this.messageReceived.next(payload);
      void this.sidebarService.updateSidebarStats();

      if (this.authService.user() === null) {
        await this.authService.fetchMe();
      }
      if (payload.authorId === this.authService.user()?.id) return;

      this.messageService.add({
        severity: 'info',
        summary: `Neue Nachricht von ${payload.authorName}`,
        detail: payload.body
      });
    });
  }

  private disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }
}
